import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, Loader2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setDone(true);
    } catch { toast.error("Please try again."); }
    setLoading(false);
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-20 pb-12 bg-neutral-950 text-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="eyebrow text-gold-400 mb-4">Contact</div>
          <h1 className="font-display font-bold text-5xl lg:text-7xl tracking-tighter">Let&rsquo;s talk.</h1>
          <p className="mt-4 text-neutral-300 max-w-xl">Whether you&rsquo;re a distributor, journalist, investor or a consumer with a question &mdash; we&rsquo;d love to hear from you.</p>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <InfoCard
              icon={MapPin}
              title="Head Office"
              body={`Scitygate Brewing Company Limited
2nd Floor
Halifax House
93–101 Bridge Street
Manchester
M3 2GX
United Kingdom

Company No. 17323558
Registered in England & Wales`}
            />
            <InfoCard icon={Mail} title="Email" body="info@scitygate.com" href="mailto:info@scitygate.com" />
            <InfoCard icon={Phone} title="Phone" body="+44 (0)161 989 4842" href="tel:+441619894842" />
            <InfoCard icon={Clock} title="Business Hours" body={`Monday – Friday · 09:00 – 18:00 GMT
Saturday – Sunday · Closed`} />
            <div className="rounded-2xl overflow-hidden border border-border aspect-[4/3]">
              <iframe
                title="SBC Location"
                src="https://www.google.com/maps?q=Halifax+House+93+Bridge+Street+Manchester+M3+2GX&output=embed"
                className="w-full h-full"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            {done ? (
              <div className="p-10 rounded-2xl bg-muted text-center" data-testid="contact-success">
                <div className="w-14 h-14 mx-auto rounded-full bg-gold-400 text-neutral-950 flex items-center justify-center font-black text-xl">✓</div>
                <div className="font-display text-2xl mt-4">Message sent</div>
                <p className="text-muted-foreground mt-2">Our team will respond within 2 business days.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="p-8 rounded-2xl border border-border space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <F label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="contact-name" />
                  <F label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="contact-email" />
                  <F label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="contact-phone" />
                  <F label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testId="contact-subject" />
                </div>
                <div>
                  <Label>Message *</Label>
                  <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid="contact-message" />
                </div>
                <button disabled={loading} className="w-full bg-neutral-950 dark:bg-white dark:text-neutral-950 text-white rounded-full h-12 font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const Label = ({ children }) => <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{children}</div>;
const F = ({ label, value, onChange, required, type = "text", testId }) => (
  <div>
    <Label>{label}</Label>
    <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid={testId} />
  </div>
);
const InfoCard = ({ icon: Icon, title, body, href }) => {
  const content = (
    <div className="flex items-start gap-4 p-5 rounded-2xl border border-border hover:border-gold-400 transition-colors">
      <div className="w-11 h-11 rounded-full bg-gold-400/15 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-gold-500" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{title}</div>
        <div className="font-display font-semibold mt-1 whitespace-pre-line text-sm">{body}</div>
      </div>
    </div>
  );
  return href ? <a href={href}>{content}</a> : content;
};
